import React, { useState, useCallback } from 'react';
import clsx from 'clsx';
import BrandLoader from '../brand/BrandLoader';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { getInitialsFromEmail } from '../../../utils/gravatarUtils';
import ProfileImage from '../../Header/ProfileImage';
import Input from '../TailwindComponents/Input';
import Dropdown, { DropdownOption } from '../TailwindComponents/Dropdown';
import {
  PiUploadLight,
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiTrashLight,
} from 'react-icons/pi';
import FieldShell, { FOCUS_RING } from '../TailwindComponents/FieldShell';
import { Well } from '../brand/Field';
import FileUploader from '../TailwindComponents/FileUploader';
import { uploadUsersImport, getImportStatus } from '../../../services/import/importApi';
import { useImportPubSub } from '../../../hooks/useImportPubSub';
import type { ImportUserRow } from '../../../types/import';
import Button from '../TailwindComponents/Button';
import { readErrorMessage, toErrorText } from '../../../utils/errorMessage';

interface InviteeItem {
  id: string;
  email: string;
  role: string;
}

interface PeopleEmailInputProps {
  id?: string;
  name?: string;
  label?: string;
  value: InviteeItem[];
  onChange: (value: InviteeItem[]) => void;
  roles: DropdownOption[];
  required?: boolean;
  disabled?: boolean;
  /** Schema-level error handed down by BaseForm. */
  error?: string;
}

type InputMode = 'manual' | 'upload';

const ALLOWED_EXTENSIONS = ['xlsx', 'xls', 'csv', 'json', 'txt', 'pdf', 'docx', 'md'];

const PeopleEmailInput: React.FC<PeopleEmailInputProps> = ({
  label,
  value = [],
  onChange,
  roles,
  required,
  disabled = false,
  error,
}) => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const [mode, setMode] = useState<InputMode>('manual');

  // File upload state
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [jobUuid, setJobUuid] = useState<string | null>(null);
  const [parseSuccess, setParseSuccess] = useState(false);
  const [parsedCount, setParsedCount] = useState(0);

  // PubSub hook for AI parsing progress
  const { progress, reset: resetProgress } = useImportPubSub({
    correlationId,
    enabled: isParsing,
    onPreviewReady: async (uuid) => {
      if (uuid || jobUuid) {
        await loadAndMergePreview(uuid || jobUuid!);
      }
    },
    onFailed: (err) => {
      setUploadError(err || t('AI parsing failed'));
      setIsParsing(false);
    },
  });

  // Load parsed preview and merge into invitee list
  const loadAndMergePreview = useCallback(async (uuid: string) => {
    try {
      const response = await getImportStatus(uuid);
      if (response.success && response.preview) {
        const rows = response.preview.rows as ImportUserRow[];
        const newInvitees = rows
          .filter((row) => row.email && !row.is_duplicate)
          .map((row, idx) => ({
            id: String(Date.now() + idx),
            email: row.email,
            role: row.role || 'full_access',
          }));

        // Merge with existing, skip duplicates
        const existingEmails = new Set(value.map((inv) => inv.email.toLowerCase()));
        const unique = newInvitees.filter(
          (inv) => !existingEmails.has(inv.email.toLowerCase())
        );

        if (unique.length > 0) {
          onChange([...value, ...unique]);
        }

        setParsedCount(unique.length);
        setParseSuccess(true);
        setIsParsing(false);
        setUploadFiles([]);

        // Auto-switch to manual mode after brief success message
        setTimeout(() => {
          setMode('manual');
          setParseSuccess(false);
          setParsedCount(0);
        }, 3000);
      }
    } catch {
      setUploadError(t('Failed to load parsed results'));
      setIsParsing(false);
    }
  }, [value, onChange, t]);

  const handleFilesChange = useCallback((files: File[]) => {
    setUploadFiles(files);
    setUploadError(null);
    setParseSuccess(false);
  }, []);

  const handleUploadAndParse = useCallback(async () => {
    const selectedFile = uploadFiles[0];
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const response = await uploadUsersImport(selectedFile);
      if (response.success && response.job_uuid) {
        setJobUuid(response.job_uuid);
        setCorrelationId(response.correlation_id || null);
        setIsUploading(false);
        setIsParsing(true);
      } else {
        const err = response.error;
        const msg = toErrorText(err) || t('Upload failed');
        setUploadError(msg);
        setIsUploading(false);
      }
    } catch (err: any) {
      const errorMsg = readErrorMessage(err, t('Upload failed'));
      setUploadError(errorMsg);
      setIsUploading(false);
    }
  }, [uploadFiles, t]);

  const resetUpload = useCallback(() => {
    setUploadFiles([]);
    setIsUploading(false);
    setIsParsing(false);
    setUploadError(null);
    setCorrelationId(null);
    setJobUuid(null);
    setParseSuccess(false);
    setParsedCount(0);
    resetProgress();
  }, [resetProgress]);

  const domainSuffix = (() => {
    if (!userInfo?.Email) return '';
    const domain = userInfo.Email.split('@')[1];
    return domain ? `@${domain}` : '';
  })();

  const isValidLocalPart = (localPart: string): boolean => {
    if (!localPart) return false;
    // Allow '+' for plus-addressing (e.g. david+test1), so one mailbox can
    // receive many distinct invites.
    const localPartRegex = /^[a-zA-Z0-9._+-]+$/;
    return localPartRegex.test(localPart);
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputText = e.target.value;
    setInputError('');

    const lastChar = inputText[inputText.length - 1];

    if (lastChar === ',' || lastChar === ' ') {
      const emailLocalPart = inputText.slice(0, -1).trim();

      if (emailLocalPart) {
        if (!isValidLocalPart(emailLocalPart)) {
          setInputError(t('Email can only contain letters, numbers, dots, plus signs, hyphens and underscores.'));
          return;
        }

        const fullEmail = `${emailLocalPart}${domainSuffix}`;
        if (isValidEmail(fullEmail)) {
          const emailExists = value.some(inv => inv.email === fullEmail);
          if (!emailExists) {
            const newId = String(value.length + 1);
            onChange([...value, { id: newId, email: fullEmail, role: '' }]);
            setInputValue('');
          } else {
            setInputError(t('This email is already added.'));
          }
        } else {
          setInputError(t('Invalid email format.'));
        }
      }
      return;
    }

    setInputValue(inputText);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const emailLocalPart = inputValue.trim();

      if (emailLocalPart) {
        if (!isValidLocalPart(emailLocalPart)) {
          setInputError(t('Email can only contain letters, numbers, dots, plus signs, hyphens and underscores.'));
          return;
        }

        const fullEmail = `${emailLocalPart}${domainSuffix}`;
        if (isValidEmail(fullEmail)) {
          const emailExists = value.some(inv => inv.email === fullEmail);
          if (!emailExists) {
            const newId = String(value.length + 1);
            onChange([...value, { id: newId, email: fullEmail, role: '' }]);
            setInputValue('');
            setInputError('');
          } else {
            setInputError(t('This email is already added.'));
          }
        } else {
          setInputError(t('Invalid email format.'));
        }
      }
    } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      const lastInvitee = value[value.length - 1];
      const localPart = lastInvitee.email.split('@')[0];
      setInputValue(localPart);
      onChange(value.slice(0, -1));
      setInputError('');
    }
  };

  const handleRemoveInvitee = (id: string) => {
    onChange(value.filter(inv => inv.id !== id));
    setInputError('');
  };

  const handleRoleChange = (id: string, roleValue: string | string[]) => {
    onChange(
      value.map(inv =>
        inv.id === id
          ? { ...inv, role: Array.isArray(roleValue) ? roleValue[0] : roleValue }
          : inv
      )
    );
  };

  const displayValue = inputValue ? `${inputValue}${domainSuffix}` : '';
  const suffixHint = displayValue.includes('@')
    ? displayValue.split('@')[1].startsWith(domainSuffix.substring(1))
      ? domainSuffix
      : ''
    : domainSuffix;

  return (
    <FieldShell
      label={label}
      required={required}
      error={error}
    >
      {/* Mode toggle commented out - Import Users uses modal wizard from AdminPage header */}
      {/* <ModeToggle> was here - manual/upload toggle removed */}

      {/* Manual email entry - always shown */}
      <div className="mb-4">
          <Input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder={t('Type email and press Enter or comma')}
            error={!!inputError}
            helperText={inputError}
            disabled={disabled}
            suffix={domainSuffix ? suffixHint : undefined}
          />
        </div>

      {/* File upload mode - commented out, using ImportUsersWizard modal flow instead
      {mode === 'upload' && (
        <div className="mb-4">
          {parseSuccess && (
            <div className="mb-3 p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg flex items-center gap-2">
              <PiCheckCircleLight className="w-5 h-5 text-success-500 flex-shrink-0" />
              <p className="text-xs text-success-700 dark:text-success-300">
                {t('Successfully added {{count}} users from file', { count: parsedCount })}
              </p>
            </div>
          )}
          {uploadError && (
            <div className="mb-3 p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg flex items-center gap-2">
              <PiWarningCircleLight className="w-5 h-5 text-danger-500 flex-shrink-0" />
              <p className="text-xs text-danger-700 dark:text-danger-300 flex-1">{uploadError}</p>
              <button type="button" onClick={() => setUploadError(null)} className="text-danger-400 hover:text-danger-600 text-xs">✕</button>
            </div>
          )}
          {isParsing && (
            <div className="flex flex-col items-center gap-3 py-8">
              <BrandLoader size="xs" className="!h-8 !w-8" />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('AI is parsing your file...')}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{progress.message || t('This usually takes 10-30 seconds')}</p>
            </div>
          )}
          {!isParsing && !parseSuccess && (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('AI will automatically parse names, emails, and roles from your file.')}</p>
              <FileUploader files={uploadFiles} onFilesChange={handleFilesChange} allowedExtensions={ALLOWED_EXTENSIONS} maxFiles={1} />
              {uploadFiles.length > 0 && (
                <Button type="button" onClick={handleUploadAndParse} disabled={isUploading} variant="primary" className="mt-3" size='small'>
                  {isUploading ? (<><BrandLoader size="xs" className="!h-4 !w-4" />{t('Uploading...')}</>) : (<><PiUploadLight className="w-4 h-4" />{t('Upload & parse with AI')}</>)}
                </Button>
              )}
            </>
          )}
        </div>
      )}
      */}

      {/* Invitees List - always visible regardless of mode */}
      {value.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-fg-muted mb-2">
            {t('Added')} ({value.length})
          </p>
          <div className="space-y-2">
            {value.map((invitee) => {
              const initials = getInitialsFromEmail(invitee.email);

              return (
                <Well key={invitee.id} className="flex gap-3 items-center">
                  {/* ProfileImage owns the whole fallback chain (picture →
                      Gravatar → initials). The DOM-swapping <img>/onError pair
                      this replaces re-implemented the last two links of it by
                      hand and could never show a stored profile picture. */}
                  <ProfileImage
                    profilePicture={null}
                    email={invitee.email}
                    initials={initials}
                    size="w-10 h-10"
                    className="flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-fg-strong truncate">{invitee.email}</p>
                  </div>
                  <div className="w-48">
                    <Dropdown
                      options={roles}
                      placeholder={t('Select role')}
                      value={invitee.role}
                      onChange={(roleValue) => handleRoleChange(invitee.id, roleValue)}
                      compact={true}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveInvitee(invitee.id)}
                    className={clsx(
                      'p-1 rounded-md text-fg-muted hover:text-danger-600 dark:hover:text-danger-400 transition-colors flex-shrink-0',
                      FOCUS_RING
                    )}
                    title={t('Remove')}
                    aria-label={t('Remove')}
                    disabled={disabled}
                  >
                    <PiTrashLight className="w-5 h-5" />
                  </button>
                </Well>
              );
            })}
          </div>
        </div>
      )}

      {/* Info Message */}
      {value.length > 0 && !isParsing && (
        <div className="p-3 bg-info-50 dark:bg-info-500/10 border border-info-200/60 dark:border-info-800/60 rounded-lg">
          <p className="text-xs text-info-800 dark:text-info-300">
            {value.length === 1
              ? t('Ready to send invitation to 1 person.')
              : t(`Ready to send invitations to ${value.length} people.`)}
          </p>
        </div>
      )}
    </FieldShell>
  );
};

export default PeopleEmailInput;
